import { useCallback, useEffect, useRef, useState } from "react";
import { getDealImages } from "../services/dealImagesApi";
import type { DealImage, DealImagesResponse } from "../types/dealImages";

interface UseDealImagesResult {
  data: DealImagesResponse | null;
  hero: DealImage | null;
  images: DealImage[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useDealImages(dealId?: number): UseDealImagesResult {
  const [data, setData] = useState<DealImagesResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Only the latest request for the current dealId may update state; older responses
  // (e.g. for a previous dealId, or from a stale refetch closure) are ignored.
  const latestRequestIdRef = useRef(0);
  const dealIdRef = useRef(dealId);

  const refetch = useCallback(async () => {
    if (!dealId) return;
    // A refetch captured before dealId changed must not invalidate the current request.
    if (dealIdRef.current !== dealId) return;
    const requestId = ++latestRequestIdRef.current;
    const isStale = () =>
      requestId !== latestRequestIdRef.current || dealIdRef.current !== dealId;
    setLoading(true);
    setError(null);
    try {
      const response = await getDealImages(dealId);
      if (isStale()) return;
      if (response?.success && response.data) {
        setData(response.data);
      } else {
        setError(
          typeof response?.message === "string"
            ? response.message
            : "Failed to load deal images",
        );
      }
    } catch (err) {
      if (isStale()) return;
      setError(err instanceof Error ? err.message : "Failed to load deal images");
    } finally {
      if (!isStale()) setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    dealIdRef.current = dealId;
    void refetch();
    return () => {
      // Invalidate any in-flight request for the previous dealId (or on unmount).
      latestRequestIdRef.current += 1;
      setLoading(false);
    };
  }, [dealId, refetch]);

  return {
    data,
    hero: data?.hero ?? null,
    images: data?.images ?? [],
    loading,
    error,
    refetch,
  };
}
